import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CallMadeOutlined,
  CallReceivedOutlined,
  CheckCircleOutline,
  Close,
  ErrorOutline,
  Refresh,
  Search,
  VisibilityOutlined,
} from '@mui/icons-material';
import { serviceLogService } from '../services/api';
import { usePortalText } from '../locales/portalTranslations';

const serviceOptions = ['Case Creation', 'Case Masters', 'AI Submission', 'AI Callback', 'Inspection Email'];

const formatDateTime = value => value
  ? new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'medium' }).format(new Date(value))
  : '—';

const JsonPanel = ({ title, value }) => {
  const pt = usePortalText();
  return (
  <Box sx={{ minWidth: 0 }}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 2,
        minHeight: 140,
        maxHeight: 360,
        overflow: 'auto',
        bgcolor: 'grey.900',
        color: 'grey.100',
        borderRadius: 1,
        fontSize: 12,
        lineHeight: 1.55,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
      }}
    >
      {value == null ? pt('noPayload') : JSON.stringify(value, null, 2)}
    </Box>
  </Box>
  );
};

const StatusChip = ({ status }) => {
  const pt = usePortalText();
  const color = status === 'Success' ? 'success' : status === 'Failed' ? 'error' : 'default';
  const icon = status === 'Success' ? <CheckCircleOutline /> : status === 'Failed' ? <ErrorOutline /> : undefined;
  return <Chip size="small" variant="outlined" color={color} icon={icon} label={status === 'Success' ? pt('success') : status === 'Failed' ? pt('failed') : status === 'Skipped' ? pt('skipped') : status} />;
};

const ServiceLogsPage = () => {
  const pt = usePortalText();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState({ total: 0, success: 0, failed: 0, skipped: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({ search: '', service: '', status: '', direction: '' });
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await serviceLogService.getLogs({
        page: page + 1,
        pageSize,
        search: appliedSearch || undefined,
        service: filters.service || undefined,
        status: filters.status || undefined,
        direction: filters.direction || undefined,
      });
      setLogs(data.logs || []);
      setTotal(data.pagination?.total || 0);
      setSummary(data.summary || { total: 0, success: 0, failed: 0, skipped: 0 });
    } catch (loadError) {
      setError(loadError.response?.data?.message || loadError.message || pt('logsError'));
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, filters.direction, filters.service, filters.status, page, pageSize]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const applySearch = event => {
    event.preventDefault();
    setPage(0);
    setAppliedSearch(filters.search.trim());
  };

  const updateFilter = field => event => {
    setPage(0);
    setFilters(current => ({ ...current, [field]: event.target.value }));
  };

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box>
          <Typography component="h1" variant="h2">{pt('serviceLogs')}</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {pt('logsHelp')}
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<Refresh />} onClick={loadLogs} disabled={loading}>{pt('refresh')}</Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
        {[
          [pt('totalCalls'), summary.total, 'primary.main'], [pt('successful'), summary.success, 'success.main'],
          [pt('failed'), summary.failed, 'error.main'], [pt('skipped'), summary.skipped, 'text.secondary'],
        ].map(([label, value, color]) => (
          <Paper key={label} variant="outlined" sx={{ p: 2 }}>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
            <Typography variant="h2" sx={{ color, mt: 0.5 }}>{value}</Typography>
          </Paper>
        ))}
      </Box>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box component="form" onSubmit={applySearch} sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(260px, 1fr) 190px 150px 150px' }, gap: 1.5 }}>
          <TextField
            value={filters.search}
            onChange={updateFilter('search')}
            placeholder={pt('searchLogs')}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
            }}
          />
          <FormControl size="small">
            <InputLabel>{pt('service')}</InputLabel>
            <Select value={filters.service} label={pt('service')} onChange={updateFilter('service')}>
              <MenuItem value="">{pt('allServices')}</MenuItem>
              {serviceOptions.map(service => <MenuItem key={service} value={service}>{service}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>{pt('direction')}</InputLabel>
            <Select value={filters.direction} label={pt('direction')} onChange={updateFilter('direction')}>
              <MenuItem value="">{pt('all')}</MenuItem><MenuItem value="Inbound">{pt('inbound')}</MenuItem><MenuItem value="Outbound">{pt('outbound')}</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>{pt('status')}</InputLabel>
            <Select value={filters.status} label={pt('status')} onChange={updateFilter('status')}>
              <MenuItem value="">{pt('allStatuses')}</MenuItem><MenuItem value="Success">{pt('success')}</MenuItem>
              <MenuItem value="Failed">{pt('failed')}</MenuItem><MenuItem value="Skipped">{pt('skipped')}</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {error && <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{error}</Alert>}

        <TableContainer sx={{ minHeight: 320 }}>
          <Table size="small" sx={{ minWidth: 1050 }}>
            <TableHead>
              <TableRow>
                <TableCell>{pt('dateTime')}</TableCell><TableCell>{pt('service')}</TableCell><TableCell>{pt('direction')}</TableCell>
                <TableCell>{pt('reference')}</TableCell><TableCell>{pt('endpoint')}</TableCell><TableCell align="right">{pt('duration')}</TableCell>
                <TableCell>{pt('status')}</TableCell><TableCell align="center">{pt('details')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 8 }}><CircularProgress size={28} /></TableCell></TableRow>
              ) : logs.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 8, color: 'text.secondary' }}>{pt('noCalls')}</TableCell></TableRow>
              ) : logs.map(log => (
                <TableRow key={log.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(log.created_at)}</TableCell>
                  <TableCell><Typography variant="body2" fontWeight={600}>{log.service}</Typography></TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={log.direction === 'Inbound' ? <CallReceivedOutlined /> : <CallMadeOutlined />}
                      label={log.direction}
                    />
                  </TableCell>
                  <TableCell>{log.reference_id || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography variant="body2" noWrap title={log.endpoint}>{log.method} {log.endpoint}</Typography>
                    {log.http_status && <Typography variant="caption" color="text.secondary">HTTP {log.http_status}</Typography>}
                  </TableCell>
                  <TableCell align="right">{log.duration_ms == null ? '—' : `${log.duration_ms} ms`}</TableCell>
                  <TableCell><StatusChip status={log.status} /></TableCell>
                  <TableCell align="center">
                    <Tooltip title={pt('viewExchange')}>
                      <IconButton size="small" color="primary" onClick={() => setSelectedLog(log)} aria-label={`View ${log.service} log`}>
                        <VisibilityOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={event => { setPageSize(Number(event.target.value)); setPage(0); }}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Paper>

      <Dialog open={Boolean(selectedLog)} onClose={() => setSelectedLog(null)} maxWidth="lg" fullWidth>
        {selectedLog && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box>
                <Typography variant="h3">{selectedLog.service}</Typography>
                <Typography variant="caption" color="text.secondary">{pt('correlationId')}: {selectedLog.correlation_id}</Typography>
              </Box>
              <IconButton onClick={() => setSelectedLog(null)} aria-label="Close service log"><Close /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
                <StatusChip status={selectedLog.status} />
                <Chip size="small" label={selectedLog.direction} />
                <Typography variant="body2" color="text.secondary">{formatDateTime(selectedLog.created_at)}</Typography>
                <Typography variant="body2" color="text.secondary">{selectedLog.duration_ms ?? '—'} ms</Typography>
              </Box>
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="caption" color="text.secondary">{pt('endpoint')}</Typography>
                <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{selectedLog.method} {selectedLog.endpoint}</Typography>
                {selectedLog.error_message && <Alert severity="error" sx={{ mt: 1.5 }}>{selectedLog.error_message}</Alert>}
              </Paper>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <JsonPanel title={pt('request')} value={selectedLog.request_payload} />
                <JsonPanel title={pt('response')} value={selectedLog.response_payload} />
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ServiceLogsPage;
